import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  redirect,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import ralewayLatin from "../assets/raleway-latin.woff2.asset.json";
import { FcAuthProvider } from "../integrations/fc/auth0";
import { SiteFooter } from "../components/site/SiteFooter";
import { SiteHeader } from "../components/site/SiteHeader";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { LocaleProvider, useLocale } from "../lib/i18n/context";
import { getLocale } from "../lib/i18n/locale.functions";
import { DEFAULT_LOCALE } from "../lib/i18n/config";
import { safeErrorDescription } from "../lib/security/safe-error";
import { registrationDocumentReloadRequired } from "../lib/event-registration";
import { PUBLIC_TRACKING_BOOTSTRAP, trackingDocumentReloadRequired } from "../lib/security/tracking-policy";
import { memberNavigationRequiresReload } from "../lib/security/member-navigation";

function NotFoundComponent() {
  const { t } = useLocale();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t.notFound.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t.notFound.text}
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t.notFound.home}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(safeErrorDescription(error));
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient; documentPathname: string | undefined; documentSession: { isAuthenticated: boolean } }>()({
  staticData: { sitemap: false },
  beforeLoad: ({ context, location, preload }) => {
    if (!preload && context.documentPathname !== undefined &&
        memberNavigationRequiresReload(context.documentPathname, location.pathname, context.documentSession.isAuthenticated) &&
        ((context.documentPathname.replace(/\/+$/, "") === "/mh") !== (location.pathname.replace(/\/+$/, "") === "/mh") ||
          registrationDocumentReloadRequired(context.documentPathname, location.pathname) ||
          trackingDocumentReloadRequired(context.documentPathname, location.pathname))) {
      // Destroy third-party code before sensitive pages. Clean member documents
      // stay untracked throughout SPA navigation, retaining memory-only tokens.
      // Use the router-built URL to preserve query/hash and avoid redirect loops.
      throw redirect({ href: location.href, reloadDocument: true, replace: true });
    }
  },
  loader: async () => ({ locale: await getLocale().catch(() => DEFAULT_LOCALE) }),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Foreign Council On Economic Relations" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Foreign Council On Economic Relations" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    scripts: [
      {
        // Inert on excluded documents and URLs containing queries/fragments.
        children: PUBLIC_TRACKING_BOOTSTRAP,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Foreign Council On Economic Relations",
          alternateName: "Foreign Council",
          url: "https://foreign-council.org",
          logo: "https://foreign-council.org/logo-dark.png",
          description:
            "International non-profit organisation for economic diplomacy, translating strategic government priorities into investment opportunities worldwide.",
          nonprofitStatus: "Nonprofit",
          areaServed: "Worldwide",
          knowsAbout: [
            "Economic Diplomacy",
            "Infrastructure Investment",
            "Critical Raw Materials",
            "Strategic Industries",
            "Defence and Security",
            "Energy",
            "Digital Infrastructure",
            "Chemicals and Pharmaceuticals",
            "Semiconductors and Electronics",
            "Automotive and Batteries",
            "EU Policy",
            "Global Finance",
            "Security Cooperation",
          ],
          member: [
            { "@type": "Person", name: "Maximilian Hoffmann", jobTitle: "President" },
            { "@type": "Person", name: "Alexander Kulitz", jobTitle: "Vice-President and Chairman" },
            { "@type": "Person", name: "Andrej Gross", jobTitle: "Vice President (hon.)" },
            { "@type": "Person", name: "Tanja Kreisz", jobTitle: "Vice President (hon.), Women in Diplomacy and Government" },
            { "@type": "Person", name: "Catherine Welling", jobTitle: "Co-Chair, Women in Diplomacy and Defence" },
          ],
        }),
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: ralewayLatin.url,
        crossOrigin: "anonymous",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const locale = Route.useLoaderData()?.locale ?? DEFAULT_LOCALE;
  return (
    <html lang={locale}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const locale = Route.useLoaderData()?.locale ?? DEFAULT_LOCALE;

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider locale={locale}>
      <FcAuthProvider>
        <div className="flex min-h-screen flex-col">
        <a href="#main-content" className="fc-skip-link">Skip to content</a>
        <SiteHeader />
        <main id="main-content" tabIndex={-1} className="flex-1 bg-background outline-none">
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
          <SiteFooter />
        </div>
      </FcAuthProvider>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
